import { Box } from '@mui/material';
import { type JSX, useState } from 'react';

import { Button } from 'Components/Buttons/Buttons';
import { Modal } from 'Components/Modal/Modal';
import { Text } from 'Components/Text/Text';
import { SubmitIrqSelections, SubmitIrqSelectionsProps, SubmitIrqSelectionsValue } from 'Components/UcrmTprm/SubmitIrqSelections/SubmitIrqSelections';
import { ICON_CLOSE, ICON_SUBMIT } from 'Config/Icons';
import { DueDiligenceAssessmentConfiguration } from 'Models/DueDiligence';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating } from 'Models/RiskRating';
import { ThirdPartyService } from 'Models/TPRM';
import { UseCase } from 'Models/Ucrm';

export interface ConfirmSubmitIrqModalProps {
    assessmentConfigurations: DueDiligenceAssessmentConfiguration[];
    assessmentSubject: ThirdPartyService | UseCase;
    hideModal: () => void;
    inherentRiskRating: RiskRating;
    module: DueDiligenceModule;
    onConfirm: (selections: SubmitIrqSelectionsValue) => void;
}

/** Used on the IRQ page. Wraps `SubmitIrqSelections`, displaying that UI only if the assessment subject is not using another assessment subject's shared due diligence assessments. */
export const ConfirmSubmitIrqModal = (props: ConfirmSubmitIrqModalProps): JSX.Element => {
    const [submitIrqSelections, setSubmitIrqSelections] = useState<SubmitIrqSelectionsValue>({ assessment_selections: [], third_party_contacts: [] });
    const isCommonAssessmentChild = props.assessmentSubject.assessment_workflow_setup.common_assessment_parent !== undefined;
    const submitIrqSelectionsBaseProps = { assessments: props.assessmentConfigurations, inherentRiskRating: props.inherentRiskRating, onSelectionsChanged: setSubmitIrqSelections };
    const submitIrqSelectionsProps: SubmitIrqSelectionsProps = props.module === ProductModule.TPRM ? { ...submitIrqSelectionsBaseProps, module: ProductModule.TPRM, contacts: (props.assessmentSubject as ThirdPartyService).vendor_contacts } : { ...submitIrqSelectionsBaseProps, module: ProductModule.UCRM, defaultQuestionnaireRespondentIds: [props.assessmentSubject.vendor_service_manager_user_id, ...props.assessmentSubject.delegates] };

    return (
        <Modal open header="Submit Inherent Risk Questionnaire" subHeader={`${props.assessmentSubject.vendor_name} - ${props.assessmentSubject.name}`}>
            <Box component="form" noValidate>
                <Text>Submitting the inherent risk questionnaire will make the due diligence questionnaires, due diligence assessments, and final review available for changes.</Text>
                {!isCommonAssessmentChild && <SubmitIrqSelections {...submitIrqSelectionsProps} />}
                {isCommonAssessmentChild && <Text noStyles>{props.module === ProductModule.UCRM ? 'This use case is using shared due diligence assessments provided by another use case. Assessments are configured via the other use case.' : 'This third-party service is using shared due diligence assessments provided by another third-party service. Assessments are configured via the other third-party service.'}</Text>}
                <div className={'modalFormButtonContainer'}>
                    <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={() => props.onConfirm(submitIrqSelections)} fontAwesomeImage={ICON_SUBMIT}>
                        Submit
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};
